import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useOsintContext } from '@/state/OsintContext';
import { ResourceType } from '@/types';

interface SelectPatchParams {
  riskKey: string;
  findingId: string;
  optionId: string;
  strategy: string;
}

interface CreatePRParams {
  riskKey: string;
  findingId: string;
}

export function useSelectPatch() {
  const axios = useAxios();
  const { isOsint } = useOsintContext();
  const [isLoading, setIsLoading] = useState(false);

  const selectPatch = useCallback(
    async (params: SelectPatchParams): Promise<void> => {
      setIsLoading(true);
      try {
        const url = isOsint ? '/remediation?osint=true' : '/remediation';
        await axios.put(url, {
          risk_key: params.riskKey,
          finding_id: params.findingId,
          option_id: params.optionId,
          strategy: params.strategy,
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Risk),
        });
        toast.success('Patch option selected');
      } catch (error) {
        console.error('[useSelectPatch] Failed:', error);
        toast.error('Failed to save patch selection');
      } finally {
        setIsLoading(false);
      }
    },
    [axios, isOsint]
  );

  return { selectPatch, isLoading };
}

export function useCreatePR() {
  const axios = useAxios();
  const { isOsint } = useOsintContext();
  const [isLoading, setIsLoading] = useState(false);

  const createPR = useCallback(
    async (params: CreatePRParams): Promise<string | null> => {
      setIsLoading(true);
      const toastId = 'create-pr';
      toast.loading('Creating pull request...', { id: toastId });
      try {
        const url = isOsint ? '/remediation/pr?osint=true' : '/remediation/pr';
        const response = await axios.post(url, {
          risk_key: params.riskKey,
          finding_id: params.findingId,
        });
        const prUrl = response.data?.pr_url;
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Risk),
        });
        toast.success('Pull request created', {
          id: toastId,
          duration: 8000,
          description: prUrl,
        });
        return prUrl ?? null;
      } catch (error) {
        console.error('[useCreatePR] Failed:', error);
        toast.error('Failed to create pull request', { id: toastId });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [axios, isOsint]
  );

  return { createPR, isLoading };
}

export function useClearPatch() {
  const axios = useAxios();
  const { isOsint } = useOsintContext();
  const [isLoading, setIsLoading] = useState(false);

  const clearPatch = useCallback(
    async (riskKey: string, findingId: string): Promise<void> => {
      setIsLoading(true);
      try {
        const url = isOsint ? '/remediation?osint=true' : '/remediation';
        await axios.delete(url, {
          data: {
            risk_key: riskKey,
            finding_id: findingId,
          },
        });
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy(ResourceType.Risk),
        });
        toast.success('Patch selection cleared');
      } catch (error) {
        console.error('[useClearPatch] Failed:', error);
        toast.error('Failed to clear patch selection');
      } finally {
        setIsLoading(false);
      }
    },
    [axios, isOsint]
  );

  return { clearPatch, isLoading };
}
